import { useState } from "react";
import SkorProtectBadge from "../SkorProtectBadge/SkorProtectBadge";
import { Button } from "../design";
import GuarantorInviteSection, {
  GuarantorInvite,
} from "../GuarantorInviteSection/GuarantorInviteSection";
import {
  getInitials,
  resolveParticipantName,
} from "../../helper/applicationHelpers";
import styles from "./JoinApplicationScreen.module.css";

type JoinApplicationScreenProps = {
  app: any;
  onAccept: (guarantorInvites: GuarantorInvite[]) => void;
  onDecline?: (guarantorInvites: GuarantorInvite[]) => void;
  onBack: () => void;
  processing: boolean;
  user: any;
  _t: (key: string, fallback?: string) => string;
  sharedLandlordNames?: string[];
};

const JoinApplicationScreen = ({
  app,
  onAccept,
  onDecline,
  onBack,
  processing,
  user,
  _t,
  sharedLandlordNames = [],
}: JoinApplicationScreenProps) => {
  const [guarantorInvites, setGuarantorInvites] = useState<GuarantorInvite[]>(
    []
  );
  const creator = app?.created_by;
  const creatorName =
    creator && typeof creator !== "string"
      ? `${creator.fname || creator.firstname || ""} ${creator.lname || creator.lastname || ""}`.trim() ||
      creator.email ||
      ""
      : "";
  const creatorEmail =
    creator && typeof creator !== "string" ? creator.email : null;
  const userEmail = user?.email?.toLowerCase();
  const participants = [...(app?.participants ?? [])].sort((a, b) => {
    const rank = (participant: any) => {
      const email = participant?.email?.toLowerCase();
      if (creatorEmail && email === creatorEmail.toLowerCase()) return 0;
      if (userEmail && email === userEmail) return 1;
      return 2;
    };
    return rank(a) - rank(b);
  });

  const isDecisionMode = typeof onDecline === "function";
  const appTitle = app?.title || `${creatorName} ${_t("group")}`;
  const inviteTitle = isDecisionMode
    ? `${creatorName} ${_t("invitedYouTo", "invited you to")} ${appTitle}`
    : `${_t("youArePartOf")} ${appTitle}`;

  return (
    <main className={styles.card}>
      <Button
        className={styles.backButton}
        variant="ghost"
        size="sm"
        onClick={onBack}
        disabled={processing}
      >
        <span aria-hidden="true">‹</span> {_t("back")}
      </Button>

      <div className={styles.headingBlock}>
        <div className={styles.titleRow}>
          <h1>{inviteTitle}</h1>
          {app?.skor_protect && (
            <span className={styles.protectBadge}>
              <SkorProtectBadge />
            </span>
          )}
        </div>
        {sharedLandlordNames.length > 0 && (
          <div className={styles.sharedChips} aria-label="already shared">
            {sharedLandlordNames.map((name) => (
              <span key={name} className={styles.sharedChip}>
                {_t("alreadySharedWith", "Already shared with")} {name}
              </span>
            ))}
          </div>
        )}
        <p>
          {creatorName} {_t("invitedYouToThisApplication")}{" "}
          {!isDecisionMode && _t("yourVerifiedProfileHasBeenAddedToIt")}
        </p>
      </div>

      <section className={styles.people} aria-label={_t("applicationMembers")}>
        {participants.map((participant: any, index: number) => {
          const email = participant?.email || "";
          const isOwner =
            !!creatorEmail &&
            email.toLowerCase() === creatorEmail.toLowerCase();
          const isMe = !!userEmail && email.toLowerCase() === userEmail;
          const name = isMe ? _t("you") : resolveParticipantName(participant);

          return (
            <div className={styles.personRow} key={email || index}>
              <span className={styles.avatar}>{getInitials(name)}</span>
              <div className={styles.personDetails}>
                <div className={styles.personName}>
                  <strong>{name}</strong>
                  <span>{isOwner ? _t("leadTenant") : _t("tenant")}</span>
                </div>
                <p>{email}</p>
              </div>
            </div>
          );
        })}

        <GuarantorInviteSection
          currentUserEmail={user?.email}
          invites={guarantorInvites}
          onChange={setGuarantorInvites}
          _t={_t}
          variant="join"
        />
      </section>

      <footer
        className={`${styles.footer} ${isDecisionMode ? styles.footerDecision : ""}`}
      >
        {isDecisionMode ? (
          <>
            <div className={styles.decisionButtons}>
              <button
                type="button"
                className={styles.declineButton}
                onClick={() => onDecline?.(guarantorInvites)}
                disabled={processing}
              >
                {_t("decline", "Decline")}
              </button>
              <button
                type="button"
                onClick={() => onAccept(guarantorInvites)}
                disabled={processing}
              >
                {processing ? "..." : _t("acceptAndJoin", "Accept and join")}
              </button>
            </div>
            <p className={styles.manageLater}>
              {_t(
                "manageChangesLaterApplicationsTab",
                "You can manage changes later from the Applications tab in your profile."
              )}
            </p>
          </>
        ) : (
          <>
            <p>{_t("manageGuarantorsAnytimeFromYourProfile")}</p>
            <Button
              variant="primary"
              size="lg"
              onClick={() => onAccept(guarantorInvites)}
              disabled={processing}
            >
              {processing ? "..." : _t("finish")}
            </Button>
          </>
        )}
      </footer>
    </main>
  );
};

export default JoinApplicationScreen;
